import { getPayloadStateValue } from './parameter-sources';

const PRIVACY_ALLOWED_RE = /^1(Y|N)YN$/;

const CONDITION_OPERATORS = {
  equals: (actual, expected) => actual === expected,
  contains: (actual, expected) => actual.includes(expected),
  doesNotContain: (actual, expected) => !actual.includes(expected),
  doesNotMatchRegex: (actual, expected) => !expected.test(actual),
};

function compare(actualValue, operator, expectedValue) {
  const actual = String(actualValue ?? '');

  return CONDITION_OPERATORS[operator](actual, expectedValue);
}

function matchesConditions(rule, state) {
  if (rule.conditions.length === 0) return true;

  return rule.conditions.every(({ key, operator, expectedValue }) =>
    compare(getPayloadStateValue(key, state), operator, expectedValue)
  );
}

const SERVER_SIDE_PAYLOAD_TRIGGER_RULES_BY_EVENT_TYPE = {
  action_completed: [
    {
      name: 'EchoPark Offer',
      conditions: [
        { key: 'page_name', operator: 'equals', expectedValue: 'multi_offer_results' },
        { key: 'creative_id', operator: 'equals', expectedValue: 'multi-offer-results-page' },
        { key: 'action_category', operator: 'equals', expectedValue: 'system' },
        { key: 'action_cause', operator: 'equals', expectedValue: 'page_load' },
        { key: 'action_name', operator: 'equals', expectedValue: 'receive_epo' },
        { key: 'value', operator: 'equals', expectedValue: 'echopark' },
        { key: 'subaction_name', operator: 'contains', expectedValue: 'offer_brand_' },
      ],
    },
    {
      name: 'ADSOL_save_vehicle_action_completed',
      conditions: [
        { key: 'action_name', operator: 'equals', expectedValue: 'save_vehicle' },
        { key: 'subaction_name', operator: 'equals', expectedValue: 'save_vin' },
      ],
    },
    {
      name: 'MRK OEM clickout',
      conditions: [
        { key: 'subaction_name', operator: 'equals', expectedValue: 'oem_site_clickout' },
        { key: 'page_name', operator: 'equals', expectedValue: 'new_model_highlights' },
      ],
    },
    {
      name: 'RTB - Core Clickout Event',
      conditions: [
        { key: 'subaction_name', operator: 'equals', expectedValue: 'oem_site_clickout' },
        { key: 'page_name', operator: 'equals', expectedValue: 'new_model_core' },
        { key: 'us_privacy', operator: 'doesNotMatchRegex', expectedValue: PRIVACY_ALLOWED_RE },
      ],
    },
    {
      name: 'RTB - Highlights Clickout Event',
      conditions: [
        { key: 'subaction_name', operator: 'equals', expectedValue: 'oem_site_clickout' },
        { key: 'page_name', operator: 'equals', expectedValue: 'new_model_highlights' },
        { key: 'us_privacy', operator: 'doesNotMatchRegex', expectedValue: PRIVACY_ALLOWED_RE },
      ],
    },
    {
      name: 'RTB listing',
      conditions: [
        { key: 'page_name', operator: 'contains', expectedValue: '_car_inventory_srp' },
        { key: 'action_name', operator: 'equals', expectedValue: 'list' },
        { key: 'creative_id', operator: 'doesNotContain', expectedValue: 'check' },
      ],
    },
    {
      name: 'RTB srp',
      conditions: [
        { key: 'page_name', operator: 'contains', expectedValue: 'car_inventory_srp' },
        { key: 'action_name', operator: 'equals', expectedValue: 'list' },
      ],
    },
    {
      name: 'RTB wishlist',
      conditions: [
        { key: 'action_name', operator: 'equals', expectedValue: 'save_vehicle' },
        { key: 'subaction_name', operator: 'equals', expectedValue: 'save_vin' },
      ],
    },
    {
      name: 'save_vehicle_action_completed (privacy)',
      conditions: [
        { key: 'action_name', operator: 'equals', expectedValue: 'save_vehicle' },
        { key: 'subaction_name', operator: 'equals', expectedValue: 'save_vin' },
        { key: 'us_privacy', operator: 'doesNotMatchRegex', expectedValue: PRIVACY_ALLOWED_RE },
      ],
    },
    {
      name: 'Submit Search (privacy)',
      conditions: [
        { key: 'action_name', operator: 'contains', expectedValue: 'view_search_result' },
        { key: 'event_type', operator: 'contains', expectedValue: 'action_completed' },
        { key: 'subaction_name', operator: 'contains', expectedValue: 'view_autocomplete_result' },
        { key: 'us_privacy', operator: 'doesNotMatchRegex', expectedValue: PRIVACY_ALLOWED_RE },
      ],
    },
  ],
  action_end: [
    {
      name: 'Lead Submission (Privacy)',
      conditions: [
        { key: 'action_name', operator: 'equals', expectedValue: 'lead_submission' },
        { key: 'us_privacy', operator: 'doesNotMatchRegex', expectedValue: PRIVACY_ALLOWED_RE },
      ],
    },
    {
      name: 'RTB order purchase',
      conditions: [
        { key: 'action_name', operator: 'equals', expectedValue: 'lead_submission' },
        { key: 'subaction_name', operator: 'contains', expectedValue: 'display_thank_you' },
      ],
    },
  ],
  action_progress: [
    {
      name: 'Appraisal Offer',
      conditions: [
        { key: 'page_name', operator: 'equals', expectedValue: 'used_model_mydp_tmv_appraiser_style' },
        { key: 'subaction_name', operator: 'equals', expectedValue: 'epo_offer_vin' },
        { key: 'creative_id', operator: 'equals', expectedValue: 'edm-entry-partner-offer' },
        { key: 'action_category', operator: 'equals', expectedValue: 'user' },
        { key: 'action_name', operator: 'contains', expectedValue: 'receive_epo' },
      ],
    },
    {
      name: 'RTB - estimate order',
      conditions: [
        { key: 'page_name', operator: 'equals', expectedValue: 'used_model_mydp_tmv_appraiser_style' },
        { key: 'subaction_name', operator: 'equals', expectedValue: 'epo_estimated_offer_vin' },
        { key: 'creative_id', operator: 'equals', expectedValue: 'edm-entry-epo-estimated-offer' },
        { key: 'action_category', operator: 'equals', expectedValue: 'user' },
        { key: 'action_name', operator: 'equals', expectedValue: 'receive_epo' },
        { key: 'action_cause', operator: 'equals', expectedValue: 'button_click' },
      ],
    },
    {
      name: 'RTB - no offer',
      conditions: [
        { key: 'page_name', operator: 'equals', expectedValue: 'used_model_mydp_tmv_appraiser_style' },
        { key: 'subaction_name', operator: 'equals', expectedValue: 'epo_no_offer_vin' },
        { key: 'creative_id', operator: 'equals', expectedValue: 'edm-entry-partner-offer' },
        { key: 'action_category', operator: 'equals', expectedValue: 'user' },
        { key: 'action_name', operator: 'equals', expectedValue: 'receive_epo' },
        { key: 'action_cause', operator: 'equals', expectedValue: 'button_click' },
      ],
    },
    {
      name: 'RTB - startorder',
      conditions: [
        { key: 'page_name', operator: 'equals', expectedValue: 'used_model_mydp_tmv_appraiser_style' },
        { key: 'subaction_name', operator: 'equals', expectedValue: 'submit_style' },
      ],
    },
    {
      name: 'RTB - success order',
      conditions: [
        { key: 'page_name', operator: 'equals', expectedValue: 'used_model_mydp_tmv_appraiser_style' },
        { key: 'subaction_name', operator: 'equals', expectedValue: 'epo_offer_vin' },
        { key: 'creative_id', operator: 'equals', expectedValue: 'edm-entry-partner-offer' },
        { key: 'action_category', operator: 'equals', expectedValue: 'user' },
        { key: 'action_name', operator: 'equals', expectedValue: 'receive_epo' },
      ],
    },
  ],
  page_enter: [
    {
      name: 'page_enter',
      conditions: [],
    },
    {
      name: 'RTB offer',
      conditions: [{ key: 'page_name', operator: 'equals', expectedValue: '_model_car_inventory_vin_detail' }],
    },
  ],
  widget_view: [
    {
      name: 'Appraisal Value - EPO Valid Offers Only',
      conditions: [{ key: 'creative_id', operator: 'equals', expectedValue: 'edm-entry-epo-offer' }],
    },
  ],
};

const SERVER_SIDE_PAYLOAD_REGEX_TRIGGER_RULES = [
  {
    name: 'ADSOL Multi Lead Submission',
    eventTypeRegex: /^action_end.*/,
    conditions: [{ key: 'action_name', operator: 'equals', expectedValue: 'lead_submission' }],
  },
];

export function matchesServerSidePayloadTrigger(state) {
  const eventType = getPayloadStateValue('event_type', state);
  const eventTypeRules = SERVER_SIDE_PAYLOAD_TRIGGER_RULES_BY_EVENT_TYPE[eventType] || [];
  const eventTypeRuleMatches = eventTypeRules.some(rule => matchesConditions(rule, state));
  const regexRuleMatches = SERVER_SIDE_PAYLOAD_REGEX_TRIGGER_RULES.some(
    rule => rule.eventTypeRegex.test(String(eventType)) && matchesConditions(rule, state)
  );

  return eventTypeRuleMatches || regexRuleMatches;
}
